import { memo } from 'react';

import {
  PreviewableImage,
  ResourceNameLink,
  extractLastNameComponent,
  isResourceName,
} from '@spotify/confidence-core-react';
import { ChatEventUserMessageFragment } from '@spotify/confidence-graphql';

import {
  type ChatAttachment,
  type ImageAttachment,
  attachmentKey,
} from '../../types';
import { AttachmentChip, ResourceAttachmentChip } from '../AttachmentChip';
import { PlainTextContent } from '../PlainTextContent';

import styles from './UserMessage.module.css';

interface UserMessageProps {
  content?: string | null;
  parts?: ChatEventUserMessageFragment['parts'];
  attachments?: ChatAttachment[];
}

export const UserMessage = memo(
  ({ content, parts, attachments = [] }: UserMessageProps) => {
    const attachmentParts = (parts ?? []).filter(p => p.resource);
    const inlineResourceParts = attachmentParts.filter(part =>
      isResourceName(part.resource ?? '', 'pulses'),
    );
    const attachmentResourceParts = attachmentParts.filter(
      part => !isResourceName(part.resource ?? '', 'pulses'),
    );
    const imageParts = (parts ?? []).filter(p => p.imageUri);
    const docAttachments = attachments.filter(
      a => a.kind === 'resolved' || a.kind === 'pending',
    );
    const imageAttachments = attachments.filter(
      (a): a is ImageAttachment => a.kind === 'image',
    );
    const textFromParts = (parts ?? [])
      .map(p => p.text)
      .filter((t): t is string => !!t)
      .join('\n\n');
    const bodyText = textFromParts || content || '';

    if (
      !bodyText &&
      attachmentParts.length === 0 &&
      imageParts.length === 0 &&
      docAttachments.length === 0 &&
      imageAttachments.length === 0
    ) {
      return null;
    }

    return (
      <div className={styles.root}>
        <div className={styles.body}>
          {(docAttachments.length > 0 ||
            attachmentResourceParts.length > 0) && (
            <div className={styles.attachments}>
              {docAttachments.map(attachment => (
                <AttachmentChip
                  key={attachmentKey(attachment)}
                  attachment={attachment}
                />
              ))}
              {attachmentResourceParts.map(part => (
                <ResourceAttachmentChip
                  key={part.resource ?? ''}
                  resourceName={part.resource ?? ''}
                />
              ))}
            </div>
          )}
          {(imageParts.length > 0 || imageAttachments.length > 0) && (
            <div className={styles.images}>
              {imageParts.map((part, idx) => (
                <PreviewableImage
                  key={part.imageUri ?? idx}
                  src={part.imageUri ?? ''}
                  alt="Attachment"
                  className={styles.image}
                  borderRadius="base"
                />
              ))}
              {imageAttachments.map(a => (
                <PreviewableImage
                  key={attachmentKey(a)}
                  src={a.imageUri}
                  alt="Attachment"
                  className={styles.image}
                  borderRadius="base"
                />
              ))}
            </div>
          )}
          {(inlineResourceParts.length > 0 || bodyText) && (
            <div className={styles.text}>
              {inlineResourceParts.map((part, index) => {
                const resourceName = part.resource ?? '';
                const label =
                  extractLastNameComponent(resourceName) ?? resourceName;
                return (
                  <span
                    key={`${resourceName}-${index}`}
                    className={styles.inlineResource}
                  >
                    @<ResourceNameLink name={resourceName} text={label} />
                  </span>
                );
              })}
              {bodyText && (
                <div className={styles.content}>
                  <PlainTextContent>{bodyText}</PlainTextContent>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  },
);
UserMessage.displayName = 'UserMessage';
